// Portfolio Administration v1.0 PR 3 §7.3 + Loan record foundations —
// Parties tab. Renders the 8-section roster (Borrower / Sellers /
// Guarantors / Broker / Vendors / Local Enforcers / Phoenix internal team /
// Other contacts) with server-resolved names + contact info. Sections with
// dedicated tabs (Vendors / Local Enforcers / Phoenix team) are read-only
// summaries with a "manage on …" cross-link. "Show former parties" reveals
// soft-removed rows (muted, with their effective date range); "Copy parties
// from deal" runs the idempotent deal-roster seed for loans created before
// the closing handoff started seeding automatically.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, OverflowMenu } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { PartyRosterSection } from "../parties/PartyRosterSection";
import { BorrowerPortalAccessCard } from "../parties/BorrowerPortalAccessCard";
import { SellerPortalAccessCard } from "../parties/SellerPortalAccessCard";
import { BrokerPortalAccessCard } from "../parties/BrokerPortalAccessCard";
import { GuarantySendPanel } from "../parties/GuarantySendPanel";
import {
  useLoanParties,
  useSeedPartiesFromDeal,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type { LoanPartyAssignmentDto, LoanSummaryDto } from "@/api/portfolioAdmin/types";

interface PartiesTabProps {
  loan: LoanSummaryDto;
}

const BORROWER_ROLES = new Set(["Borrower"]);
const SELLER_ROLES = new Set(["Seller"]);
const GUARANTOR_ROLES = new Set(["Guarantor"]);
const BROKER_ROLES = new Set(["Broker"]);

function partition(parties: LoanPartyAssignmentDto[]) {
  const buckets: Record<string, LoanPartyAssignmentDto[]> = {
    borrower: [],
    seller: [],
    guarantor: [],
    broker: [],
    vendor: [],
    enforcer: [],
    phoenix: [],
    other: [],
  };
  for (const p of parties) {
    if (p.counterpartyType === "Vendor") buckets.vendor.push(p);
    else if (p.counterpartyType === "LocalEnforcer") buckets.enforcer.push(p);
    else if (p.counterpartyType === "PhoenixUser") buckets.phoenix.push(p);
    else if (BORROWER_ROLES.has(p.roleOnLoan)) buckets.borrower.push(p);
    else if (SELLER_ROLES.has(p.roleOnLoan)) buckets.seller.push(p);
    else if (GUARANTOR_ROLES.has(p.roleOnLoan)) buckets.guarantor.push(p);
    else if (BROKER_ROLES.has(p.roleOnLoan)) buckets.broker.push(p);
    else buckets.other.push(p);
  }
  return buckets;
}

export function PartiesTab({ loan }: PartiesTabProps) {
  const [showFormer, setShowFormer] = useState(false);
  const { data, isLoading, isError } = useLoanParties(loan.id, showFormer);
  const toast = useToast();
  const auth = useAuth();
  const canManage = auth.hasPermission("Loans.Manage") || auth.hasRoleCode("ADMIN");
  const seed = useSeedPartiesFromDeal(loan.id);

  const parties = data ?? [];
  const b = partition(parties);
  const rosterEmpty = parties.length === 0;

  const onCopyFromDeal = async () => {
    try {
      const result = await seed.mutateAsync();
      toast.success(
        result.createdCount > 0
          ? `${result.createdCount} part${result.createdCount === 1 ? "y" : "ies"} copied from the deal.`
          : "Roster already matches the deal — nothing to copy.",
      );
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  if (isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading parties…</p>
      </Card>
    );
  }
  if (isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0">
          Could not load the Parties roster. Refresh to retry.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="parties-tab">
      <div className="flex items-center justify-end gap-space-3">
        <label className="flex items-center gap-space-1 text-small text-onyx">
          <input
            type="checkbox"
            checked={showFormer}
            onChange={(e) => setShowFormer(e.target.checked)}
            data-testid="parties-show-former"
          />
          Show former parties
        </label>
        {canManage && (
          <OverflowMenu
            label="Parties actions"
            data-testid="parties-overflow"
            items={[
              {
                label: "Copy parties from deal",
                onSelect: () => void onCopyFromDeal(),
                disabled: seed.isPending,
              },
            ]}
          />
        )}
      </div>

      {rosterEmpty && canManage && (
        <Card data-testid="parties-empty-state">
          <h3 className="font-display font-bold text-h4 m-0">No parties on this loan yet</h3>
          <p className="text-small text-gray-mid mt-space-1 m-0">
            The deal this loan came from already knows the borrower, seller and
            broker — copy them over instead of re-entering them.
          </p>
          <Button
            variant="primary"
            className="mt-space-3"
            onClick={() => void onCopyFromDeal()}
            disabled={seed.isPending}
            data-testid="parties-copy-from-deal"
          >
            {seed.isPending ? "Copying…" : "Copy parties from deal"}
          </Button>
        </Card>
      )}

      <PartyRosterSection
        loanId={loan.id}
        title="Borrower"
        parties={b.borrower}
        defaultCounterpartyType="Business"
        defaultRoleOnLoan="Borrower"
        testId="parties-borrower"
      />
      <BorrowerPortalAccessCard loanId={loan.id} />

      <PartyRosterSection
        loanId={loan.id}
        title="Sellers"
        parties={b.seller}
        defaultCounterpartyType="Person"
        defaultRoleOnLoan="Seller"
        testId="parties-sellers"
      />
      <SellerPortalAccessCard loanId={loan.id} />
      <PartyRosterSection
        loanId={loan.id}
        title="Guarantors"
        parties={b.guarantor}
        defaultCounterpartyType="Person"
        defaultRoleOnLoan="Guarantor"
        testId="parties-guarantors"
      />
      <GuarantySendPanel loanId={loan.id} />
      <PartyRosterSection
        loanId={loan.id}
        title="Broker"
        parties={b.broker}
        defaultCounterpartyType="Broker"
        defaultRoleOnLoan="Broker"
        testId="parties-broker"
      />
      <BrokerPortalAccessCard loanId={loan.id} />
      <PartyRosterSection
        loanId={loan.id}
        title="Vendors on this loan"
        parties={b.vendor}
        testId="parties-vendors"
        readOnly
        emptyMessage="Vendor assignments are managed on the Vendors tab (PR 4)."
      />
      <PartyRosterSection
        loanId={loan.id}
        title="Local Enforcers on this loan"
        parties={b.enforcer}
        testId="parties-enforcers"
        readOnly
        emptyMessage="Enforcer engagements are managed on the Defaults & Workout tab (PR 4)."
      />
      <PartyRosterSection
        loanId={loan.id}
        title="Phoenix internal team"
        parties={b.phoenix}
        testId="parties-phoenix"
        readOnly
        emptyMessage="Phoenix internal assignments are shown in the 7-pill widget at the top of the page."
      />
      <PartyRosterSection
        loanId={loan.id}
        title="Other contacts"
        parties={b.other}
        defaultCounterpartyType="OtherContact"
        defaultRoleOnLoan="Other"
        testId="parties-other"
      />

      <Card>
        <p className="text-caption text-gray-mid m-0">
          Manage vendor relationships on the{" "}
          <Link
            to={`/portfolio-admin/loans/${loan.id}?tab=vendors`}
            className="text-gold-shadow hover:underline"
          >
            Vendors tab
          </Link>{" "}
          and enforcer engagements on the{" "}
          <Link
            to={`/portfolio-admin/loans/${loan.id}?tab=defaults`}
            className="text-gold-shadow hover:underline"
          >
            Defaults &amp; Workout tab
          </Link>
          .
        </p>
      </Card>
    </div>
  );
}
